// ═══════════════════════════════════════════════════════════════
// Theme Router - Centralized Route Resolution
// Maps URL paths to theme views based on theme type
// ═══════════════════════════════════════════════════════════════

import type { ThemeType } from './theme-contract';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

/**
 * Supported view types across all themes
 */
export type ThemeViewType =
    | 'HOME'
    | 'PRODUCT'
    | 'COLLECTION'
    | 'CART'
    | 'CHECKOUT'
    | 'SEARCH'
    | 'MENU'
    | 'RESERVATION'
    | 'PAGE'
    | 'NOT_FOUND'
    | 'LOGIN'
    | 'REGISTER'
    | 'ACCOUNT';

/**
 * Resolved route information
 */
export interface ResolvedRoute {
    /** The view to render */
    view: ThemeViewType;
    /** Resource slug (product, collection, page) */
    slug?: string;
    /** Search query */
    query?: string;
    /** Additional params */
    params?: Record<string, string>;
}

// ─────────────────────────────────────────────────────────────
// Route Resolution
// ─────────────────────────────────────────────────────────────

/**
 * Resolves a URL path to a theme view based on the theme type
 * 
 * @param path - URL path segments (e.g., ['product', 'my-shirt'])
 * @param themeType - The type of theme (ecommerce, restaurant, etc.)
 * @returns Resolved route with view and params
 * 
 * @example
 * resolveThemeRoute(['product', 'my-shirt'], 'ecommerce')
 * // Returns: { view: 'PRODUCT', slug: 'my-shirt' }
 */
export function resolveThemeRoute(
    path: string[] | undefined,
    themeType: ThemeType
): ResolvedRoute {
    // No path = Home
    if (!path || path.length === 0) {
        return { view: 'HOME' };
    }

    // Force Checkout View
    if (path[0] === 'checkout') {
        return { view: 'CHECKOUT' };
    }

    const [segment1, segment2] = path;

    // ═══════════════════════════════════════════════════════════
    // E-Commerce Routes
    // ═══════════════════════════════════════════════════════════
    if (themeType === 'ecommerce') {
        // Product page: /product/:slug
        if (segment1 === 'product' && segment2) {
            return { view: 'PRODUCT', slug: segment2 };
        }

        // Collection page: /collection/:slug
        if (segment1 === 'collection') {
            return { view: 'COLLECTION', slug: segment2 || undefined };
        }

        // Search page: /search or /search?q=...
        if (segment1 === 'search') {
            return { view: 'SEARCH', query: segment2 || undefined };
        }

        // Cart page: /cart
        if (segment1 === 'cart') {
            return { view: 'CART' };
        }

        // Checkout page: /checkout
        if (segment1 === 'checkout') {
            return { view: 'CHECKOUT' };
        }

        // Auth pages
        if (segment1 === 'login') {
            return { view: 'LOGIN' };
        }
        if (segment1 === 'register' || segment1 === 'signup') {
            return { view: 'REGISTER' };
        }
        if (segment1 === 'account' || segment1 === 'profile') {
            return { view: 'ACCOUNT' };
        }

        // Products listing (legacy): /products
        if (segment1 === 'products') {
            if (segment2) {
                return { view: 'PRODUCT', slug: segment2 };
            }
            return { view: 'COLLECTION' };
        }
    }

    // ═══════════════════════════════════════════════════════════
    // Restaurant Routes
    // ═══════════════════════════════════════════════════════════
    if (themeType === 'restaurant') {
        // Menu page: /menu or /menu/:category
        if (segment1 === 'menu') {
            return { view: 'MENU', params: { category: segment2 || 'all' } };
        }

        // Reservation page: /reservation
        if (segment1 === 'reservation' || segment1 === 'book') {
            return { view: 'RESERVATION' };
        }

        // Order page: /order (same as cart)
        if (segment1 === 'order') {
            return { view: 'CART' };
        }
    }

    // ═══════════════════════════════════════════════════════════
    // Services / Portfolio Routes
    // ═══════════════════════════════════════════════════════════
    if (themeType === 'services' || themeType === 'portfolio') {
        // Service detail: /service/:slug
        if (segment1 === 'service' && segment2) {
            return { view: 'PRODUCT', slug: segment2 };
        }

        // Work/project detail: /work/:slug or /project/:slug
        if ((segment1 === 'work' || segment1 === 'project') && segment2) {
            return { view: 'PRODUCT', slug: segment2 };
        }

        // Contact page: /contact
        if (segment1 === 'contact') {
            return { view: 'PAGE', slug: 'contact' };
        }
    }

    // ═══════════════════════════════════════════════════════════
    // Generic Routes (All Themes)
    // ═══════════════════════════════════════════════════════════

    // Static pages: /about, /contact, /terms, etc.
    const staticPages = ['about', 'contact', 'terms', 'privacy', 'faq'];
    if (segment1 && staticPages.includes(segment1)) {
        return { view: 'PAGE', slug: segment1 };
    }

    // Generic page: /page/:slug
    if (segment1 === 'page' && segment2) {
        return { view: 'PAGE', slug: segment2 };
    }

    // ═══════════════════════════════════════════════════════════
    // Fallback: Treat as page slug
    // ═══════════════════════════════════════════════════════════
    // If nothing matched, try to render as a page
    return { view: 'PAGE', slug: segment1 };
}

// ─────────────────────────────────────────────────────────────
// URL Building (Reverse Routing)
// ─────────────────────────────────────────────────────────────

/**
 * Builds a URL path from a view and params
 * 
 * @param view - The view type
 * @param params - Route params (slug, query, etc.)
 * @param themeType - The theme type for context
 * @returns URL path string
 * 
 * @example
 * buildThemeUrl('PRODUCT', { slug: 'my-shirt' }, 'ecommerce')
 * // Returns: '/product/my-shirt'
 */
export function buildThemeUrl(
    view: ThemeViewType,
    params?: { slug?: string; query?: string },
    themeType: ThemeType = 'ecommerce'
): string {
    switch (view) {
        case 'HOME':
            return '/';

        case 'PRODUCT':
            return params?.slug ? `/product/${params.slug}` : '/';

        case 'COLLECTION':
            return params?.slug ? `/collection/${params.slug}` : '/collection';

        case 'SEARCH':
            return params?.query ? `/search?q=${encodeURIComponent(params.query)}` : '/search';

        case 'CART':
            return themeType === 'restaurant' ? '/order' : '/cart';

        case 'CHECKOUT':
            return '/checkout';

        case 'MENU':
            return params?.slug ? `/menu/${params.slug}` : '/menu';

        case 'RESERVATION':
            return '/reservation';

        case 'PAGE':
            return params?.slug ? `/${params.slug}` : '/';

        default:
            return '/';
    }
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

/**
 * Check if a view is available for a given theme type
 */
export function isViewAvailable(view: ThemeViewType, themeType: ThemeType): boolean {
    const ecommerceViews: ThemeViewType[] = ['HOME', 'PRODUCT', 'COLLECTION', 'CART', 'CHECKOUT', 'SEARCH', 'PAGE'];
    const restaurantViews: ThemeViewType[] = ['HOME', 'MENU', 'CART', 'RESERVATION', 'PAGE'];
    const servicesViews: ThemeViewType[] = ['HOME', 'PRODUCT', 'PAGE'];
    const portfolioViews: ThemeViewType[] = ['HOME', 'PRODUCT', 'COLLECTION', 'PAGE'];

    switch (themeType) {
        case 'ecommerce':
            return ecommerceViews.includes(view);
        case 'restaurant':
            return restaurantViews.includes(view);
        case 'services':
            return servicesViews.includes(view);
        case 'portfolio':
            return portfolioViews.includes(view);
        default:
            return view === 'HOME' || view === 'PAGE';
    }
}

export default resolveThemeRoute;
